import { useAuthStore } from '@/stores/auth';
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';

/**
 * Navigation guard for routes that require authentication.
 *
 * Redirects unauthenticated users to the login page.
 */
export function authGuard(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext,
): void {
    const authStore = useAuthStore();

    // Check if route requires authentication
    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

    if (requiresAuth && !authStore.isAuthenticated) {
        // Store intended destination for redirect after login
        sessionStorage.setItem('intended', to.fullPath);

        next({
            name: 'login',
            query: { redirect: to.fullPath },
        });
        return;
    }

    next();
}

/**
 * Navigation guard for routes that require the user to be a guest.
 *
 * Redirects authenticated users to the dashboard.
 */
export function guestGuard(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext,
): void {
    const authStore = useAuthStore();

    // Check if route requires guest
    const requiresGuest = to.matched.some(
        (record) => record.meta.requiresGuest,
    );

    if (requiresGuest && authStore.isAuthenticated) {
        // Check for intended destination
        const intended = sessionStorage.getItem('intended');
        if (intended) {
            sessionStorage.removeItem('intended');
            next(intended);
            return;
        }

        next({ name: 'dashboard' });
        return;
    }

    next();
}

/**
 * Navigation guard for routes that require specific roles.
 *
 * Redirects users without required roles to the dashboard with an error.
 */
export function roleGuard(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext,
): void {
    const authStore = useAuthStore();

    // Get required roles from all matched routes
    const requiredRoles = to.matched.reduce<string[]>((roles, record) => {
        if (record.meta.roles) {
            return [...roles, ...record.meta.roles];
        }
        return roles;
    }, []);

    if (requiredRoles.length === 0) {
        next();
        return;
    }

    // Check if user has any of the required roles
    if (!authStore.hasAnyRole(requiredRoles)) {
        console.warn(
            `Access denied: User lacks required roles [${requiredRoles.join(', ')}]`,
        );

        // Redirect to dashboard with error
        next({
            name: 'dashboard',
            query: { error: 'unauthorized' },
        });
        return;
    }

    next();
}

/**
 * Navigation guard for routes that require specific abilities.
 *
 * Redirects users without required abilities to the dashboard with an error.
 */
export function abilityGuard(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext,
): void {
    const authStore = useAuthStore();

    // Get required abilities from all matched routes
    const requiredAbilities = to.matched.reduce<string[]>(
        (abilities, record) => {
            if (record.meta.abilities) {
                return [...abilities, ...record.meta.abilities];
            }
            return abilities;
        },
        [],
    );

    if (requiredAbilities.length === 0) {
        next();
        return;
    }

    // Check if user has any of the required abilities
    if (!authStore.hasAnyAbility(requiredAbilities)) {
        console.warn(
            `Access denied: User lacks required abilities [${requiredAbilities.join(', ')}]`,
        );

        // Redirect to dashboard with error
        next({
            name: 'dashboard',
            query: { error: 'unauthorized' },
        });
        return;
    }

    next();
}

/**
 * Navigation guard to verify email is verified for certain routes.
 */
export function emailVerifiedGuard(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext,
): void {
    const authStore = useAuthStore();

    // Skip if not authenticated or route allows unverified emails
    if (!authStore.isAuthenticated || to.meta.allowUnverified) {
        next();
        return;
    }

    // Check if email is verified
    if (!authStore.hasVerifiedEmail) {
        next({ name: 'verify-email' });
        return;
    }

    next();
}

/**
 * Utility to get the intended redirect URL after login.
 */
export function getIntendedUrl(): string | null {
    return sessionStorage.getItem('intended');
}

/**
 * Utility to clear the intended redirect URL.
 */
export function clearIntendedUrl(): void {
    sessionStorage.removeItem('intended');
}

/**
 * Utility to set an intended redirect URL.
 */
export function setIntendedUrl(url: string): void {
    sessionStorage.setItem('intended', url);
}
